/**
 * Saving a folder (CONTENT_MODEL_SPEC §2.2): upload its files, then write the
 * document repointed at the names the folder stored them under.
 *
 * The host supplies the transport; nothing here knows an endpoint, a status
 * code or a retry policy.
 */

import { parsePropertySheet } from '../scenedata/behaviors/propertiesdata';
import type { ResourceManager } from '../loaders/ResourceManager';
import { FOLDER_FILE_PREFIX } from './manifestValidator';
import { repointFolderDocument } from './folderDocumentRepoint';
import { GLTF_JSON_MIME } from '../loaders/formats/gltf21/types';
import { Logger } from '../utils/Logger';

/**
 * Upload one folder file and answer the folder-relative uri it was stored
 * under. A content-addressing store answers the same uri for the same bytes.
 */
export type FolderFileUploader = (uri: string, blob: Blob) => Promise<{ uri: string }>;

interface FolderDocument {
    files?: Array<{ uri?: unknown } | null>;
    nodes?: unknown[];
}

interface SheetFile {
    raw: unknown;
    declarations: unknown[];
    /** Declaration index → the folder file it names, and its `./` spelling. */
    references: Map<number, { uri: string; prefix: string }>;
}

/**
 * A folder's files in the order they can be stored: every file a sheet names
 * before the sheet, so the sheet is repointed at its dependencies' stored
 * names before its own bytes are taken. A dependency cycle has no such order;
 * it is reported in `cycle`, and `ordered` is empty.
 */
export interface FolderFilePlan {
    ordered: Array<[string, Blob]>;
    /** The sheets of a dependency cycle, first repeated last. */
    cycle?: string[];
    /** `blob` (the file at `uri`) with its sheet references renamed by `stored`. */
    repoint(uri: string, blob: Blob, stored: ReadonlyMap<string, string>): Blob;
}

export async function planFolderFiles(document: unknown, files: ReadonlyMap<string, Blob>): Promise<FolderFilePlan> {
    const doc = document as FolderDocument;
    const manifest = new Set(Array.isArray(doc.files) ? doc.files.map(entry => entry?.uri) : []);
    const sheets = new Map<string, SheetFile>();
    for (const uri of documentSheetUris(doc, manifest)) {
        const blob = files.get(uri);
        if (!blob) continue;
        let raw: unknown;
        try {
            raw = JSON.parse(await blob.text());
        } catch (error) {
            if (error instanceof SyntaxError) continue;
            throw error;
        }
        const parsed = parsePropertySheet(raw);
        if (!Array.isArray(parsed?.declarations)) continue;
        const references = new Map<number, { uri: string; prefix: string }>();
        parsed.declarations.forEach((value: unknown, index) => {
            const declaration = value as { name?: unknown; type?: unknown; value?: unknown } | null;
            if (declaration?.type !== 'string' || typeof declaration.name !== 'string'
                || typeof declaration.value !== 'string') return;
            const prefix = declaration.value.startsWith('./') ? './' : '';
            const dependency = declaration.value.slice(prefix.length);
            if (dependency.startsWith(FOLDER_FILE_PREFIX) && manifest.has(dependency) && files.has(dependency)) {
                references.set(index, { uri: dependency, prefix });
            }
        });
        sheets.set(uri, { raw, declarations: parsed.declarations, references });
    }

    const ordered: Array<[string, Blob]> = [];
    const done = new Set<string>();
    const active: string[] = [];
    let cycle: string[] | undefined;
    const visit = (uri: string, blob: Blob): void => {
        if (done.has(uri) || cycle) return;
        const start = active.indexOf(uri);
        if (start !== -1) {
            cycle = [...active.slice(start), uri];
            return;
        }
        active.push(uri);
        for (const dependency of sheets.get(uri)?.references.values() ?? []) {
            const bytes = files.get(dependency.uri);
            if (bytes) visit(dependency.uri, bytes);
        }
        active.pop();
        done.add(uri);
        ordered.push([uri, blob]);
    };
    for (const [uri, blob] of files) visit(uri, blob);

    return {
        ordered: cycle ? [] : ordered,
        ...(cycle ? { cycle } : {}),
        repoint(uri, blob, stored) {
            const sheet = sheets.get(uri);
            if (!sheet) return blob;
            let changed = false;
            const declarations = sheet.declarations.map((declaration, index) => {
                const reference = sheet.references.get(index);
                const next = reference && stored.get(reference.uri);
                if (!reference || next === undefined || next === reference.uri) return declaration;
                changed = true;
                return { ...declaration as Record<string, unknown>, value: reference.prefix + next };
            });
            if (!changed) return blob;
            const raw = Array.isArray(sheet.raw) ? declarations : { ...sheet.raw as Record<string, unknown>, declarations };
            return new Blob([JSON.stringify(raw)], { type: blob.type });
        },
    };
}

/** The folder files the document's `Properties` behaviors name as sheets. */
function documentSheetUris(doc: FolderDocument, manifest: ReadonlySet<unknown>): Set<string> {
    const uris = new Set<string>();
    for (const node of Array.isArray(doc.nodes) ? doc.nodes : []) {
        const behaviors = (node as { extras?: { trace?: { behaviors?: unknown } } } | null)?.extras?.trace?.behaviors;
        if (!Array.isArray(behaviors)) continue;
        for (const behavior of behaviors) {
            const properties = behavior as { '@type'?: unknown; sheets?: unknown } | null;
            if (properties?.['@type'] !== 'Properties' || !Array.isArray(properties.sheets)) continue;
            for (const ref of properties.sheets) {
                if (typeof ref !== 'string') continue;
                const uri = ref.startsWith('./') ? ref.slice(2) : ref;
                if (uri.startsWith(FOLDER_FILE_PREFIX) && manifest.has(uri)) uris.add(uri);
            }
        }
    }
    return uris;
}

/**
 * Upload `folder.files` into `folderUrl` and answer the document to write,
 * repointed at the stored names. `folder.existing` is not uploaded: the
 * folder holds it already.
 *
 * Each stored file is recorded on `resources`
 * ({@link ResourceManager.recordStoredFiles}), so the next export into this
 * folder names it instead of sending it again. A host whose document write is
 * then refused (the folder no longer holds a recorded file) calls
 * `resources.forgetStoredFiles(folderUrl)`, exports again and re-uploads.
 */
export async function uploadFolder(
    folder: { document: Blob; files: ReadonlyMap<string, Blob> },
    folderUrl: string,
    upload: FolderFileUploader,
    resources: Pick<ResourceManager, 'recordStoredFiles'>,
): Promise<Blob> {
    const document: unknown = JSON.parse(await folder.document.text());
    const plan = await planFolderFiles(document, folder.files);
    if (plan.cycle) throw new Error(`Cannot save circular sheet dependencies: ${plan.cycle.join(' -> ')}`);
    const stored = new Map<string, string>();
    const sent = new Map<string, { uri: string; blob: Blob }>();
    try {
        for (const [uri, blob] of plan.ordered) {
            const bytes = plan.repoint(uri, blob, stored);
            const { uri: storedUri } = await upload(uri, bytes);
            stored.set(uri, storedUri);
            sent.set(uri, { uri: storedUri, blob: bytes });
        }
    } finally {
        // What did upload is in the folder, even if a later upload failed.
        // A record that fails costs a re-upload, never the upload's own error.
        await resources.recordStoredFiles(folderUrl, sent).catch((error: unknown) => {
            Logger.warn('[uploadFolder] could not record the stored files; the next save sends them again', error);
        });
    }
    return new Blob([JSON.stringify(repointFolderDocument(document, stored), null, 2)], { type: GLTF_JSON_MIME });
}

/**
 * Name the files `folderUrl` already holds under their stored uri (spec §2.2):
 * each file whose bytes — after its sheet references are repointed — match
 * what was recorded for its uri leaves `files` for `existing`, and the
 * document names the stored uri. A sheet whose dependency changed has
 * different bytes, so it uploads too. Sheets that cycle name nothing, so the
 * export is the one a save without records writes, and the upload refuses it.
 */
export async function nameStoredFiles(
    document: unknown,
    files: Map<string, Blob>,
    existing: Set<string>,
    folderUrl: string,
    resources: Pick<ResourceManager, 'storedFileFor'>,
): Promise<unknown> {
    const plan = await planFolderFiles(document, files);
    if (plan.cycle) return document;
    const stored = new Map<string, string>();
    const kept = new Map<string, Blob>();
    for (const [uri, blob] of plan.ordered) {
        const bytes = plan.repoint(uri, blob, stored);
        const storedUri = await resources.storedFileFor(folderUrl, uri, bytes);
        if (storedUri === undefined) {
            kept.set(uri, bytes);
        } else {
            stored.set(uri, storedUri);
            existing.add(storedUri);
        }
    }
    // Document order, not upload order.
    for (const uri of [...files.keys()]) {
        const bytes = kept.get(uri);
        if (bytes) files.set(uri, bytes);
        else files.delete(uri);
    }
    return repointFolderDocument(document, stored);
}
